import { useState, useRef, useEffect } from "react"
import {
  Box,
  Paper,
  Typography,
  Alert,
  Button,
  CircularProgress,
  List,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Divider,
  IconButton,
  ListItemButton,
} from "@mui/material"
import { Refresh, ArrowBack, InfoOutlined } from "@mui/icons-material"
import { useAuth } from "../../context/AuthContext"
import ChatMessageList from "./ChatMessageList"
import ChatInput from "./ChatInput"
import PropertyInfoCard from "./PropertyInfoCard"
import { useUserProfile } from "./ChatUserProfile"
import useChatSignalR from "./ChatSignalRService"
import { formatDistanceToNow } from "date-fns"
import { es } from "date-fns/locale"
import type { ConversationDto } from "../../types/message"
import React from "react"

interface EmbeddedChatProps {
  propertyId: number
  realtorId?: number
}

const EmbeddedChat: React.FC<EmbeddedChatProps> = ({ propertyId, realtorId }) => {
  const { userToken } = useAuth()
  const [error, setError] = useState<string | null>(null)
  const [viewingConversation, setViewingConversation] = useState(false)
  const chatContainerRef = useRef<HTMLDivElement>(null)

  const { userProfileId } = useUserProfile({ userToken })

  const { conversations, selectedConversation, messages, loading, retryConnection, sendMessage, selectConversation } =
    useChatSignalR({
      userId: userToken?.user?.id.toString() || "",
      userProfileId,
      onError: setError,
    })

  // Filtrar conversaciones por propertyId
  const filteredConversations = conversations.filter(
    (conversation) => conversation.propertyInfo && conversation.propertyInfo.propertyId === propertyId,
  )

  const handleSelectConversation = async (conversation: ConversationDto, event: React.MouseEvent) => {
    // Prevenir el comportamiento por defecto
    event.preventDefault()

    // Seleccionar la conversación     
    await selectConversation(conversation)
    setViewingConversation(true)
  }

  const handleBackToList = () => {
    setViewingConversation(false)
  }

  // Mantener la posición de scroll cuando cambia la vista
  useEffect(() => {
    if (chatContainerRef.current) {
      // Asegurar que el contenedor de chat permanezca visible
      chatContainerRef.current.scrollIntoView({ behavior: "auto", block: "start" })
    }
  }, [viewingConversation])

  // Si hay una sola conversación para esta propiedad, seleccionarla automáticamente
  useEffect(() => {
    if (filteredConversations.length === 1 && !viewingConversation && !loading) {
      selectConversation(filteredConversations[0])
      setViewingConversation(true)
    }
  }, [filteredConversations, viewingConversation, loading, selectConversation])

  if (!userToken) {
    return (
      <Box sx={{ p: 4, textAlign: "center" }}>
        <Typography variant="body1" color="text.secondary">
          Debes iniciar sesión para ver los mensajes
        </Typography>
      </Box>
    )
  }

  return (
    <>
      <Paper
        elevation={2}
        sx={{
          borderRadius: "12px",
          p: 3,
          mb: 2,
          display: "flex",
          flexDirection: "column",
          height: "500px",
        }}
        ref={chatContainerRef}
      >
        {error && (
          <Alert
            severity="error"
            sx={{ mb: 3 }}
            action={
              <Button color="inherit" size="small" startIcon={<Refresh />} onClick={retryConnection}>
                Reintentar
              </Button>
            }
          >
            {error}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", flexGrow: 1 }}>
            <CircularProgress color="success" />
          </Box>
        ) : viewingConversation && selectedConversation ? (
          // Vista de conversación
          <>
            <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
              <IconButton onClick={handleBackToList} sx={{ mr: 1 }}>
                <ArrowBack />
              </IconButton>
              <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                {selectedConversation.participants.find((p) => p.profileId !== userProfileId)?.fullName ||
                  "Conversación"}
              </Typography>
            </Box>

            {/* Property info card */}
            {selectedConversation.propertyInfo && <PropertyInfoCard propertyInfo={selectedConversation.propertyInfo} />}

            {/* Messages */}
            <Box sx={{ flexGrow: 1, overflowY: "auto", mb: 2 }}>
              <ChatMessageList messages={messages} userProfileId={userProfileId} />
            </Box>

            {/* Input de mensaje */}
            <ChatInput
              onSendMessage={async (message) => {
                await sendMessage(message)
              }}
            />
          </>
        ) : (
          // Vista de lista de conversaciones
          <>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: "bold" }}>
              Conversaciones sobre esta propiedad
            </Typography>

            {filteredConversations.length === 0 ? (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  textAlign: "center",
                  flexGrow: 1,
                  p: 3,
                }}
              >
                <InfoOutlined sx={{ fontSize: 48, color: "#4caf50", mb: 2 }} />
                <Typography variant="h6" sx={{ mb: 1, fontWeight: "bold" }}>
                  Comunicación con el broker
                </Typography>
                <Typography variant="body1" sx={{ mb: 3, color: "text.secondary" }}>
                  Para chatear con el broker sobre esta propiedad, primero debes realizar una solicitud formal de
                  información, alquiler o compra.
                </Typography>
                <Alert severity="info" sx={{ width: "100%", maxWidth: "450px" }}>
                  <Typography variant="body2">
                    Utiliza los botones de "Comprar" o "Alquilar" en la sección de contacto para iniciar el proceso. Una
                    vez que tu solicitud sea procesada, podrás comunicarte directamente con el broker.
                  </Typography>
                </Alert>
              </Box>
            ) : (
              <List sx={{ width: "100%", bgcolor: "background.paper", overflowY: "auto", flexGrow: 1 }}>
                {filteredConversations.map((conversation, index) => {
                  const otherParticipant = conversation.participants.find((p) => p.profileId !== userProfileId)
                  return (
                    <React.Fragment key={conversation.conversationId}>
                      <ListItemButton
                        alignItems="flex-start"
                        onClick={(event) => handleSelectConversation(conversation, event)}
                        sx={{
                          backgroundColor: conversation.unreadCount > 0 ? "rgba(76, 175, 80, 0.08)" : "transparent",
                          "&:hover": {
                            backgroundColor: "rgba(0, 0, 0, 0.04)",
                          },
                        }}
                      >
                        <ListItemAvatar>
                          <Avatar
                            alt={otherParticipant?.fullName || "Usuario"}
                            src={otherParticipant?.profileImage || "/assets/images/profilePicture.png"}
                          />
                        </ListItemAvatar>
                        <ListItemText
                          primary={
                            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                              <Typography
                                component="span"
                                variant="body1"
                                sx={{ fontWeight: conversation.unreadCount > 0 ? "bold" : "normal" }}
                              >
                                {otherParticipant?.fullName || "Usuario"}
                              </Typography>
                              <Typography component="span" variant="caption" color="text.secondary">
                                {formatDistanceToNow(new Date(conversation.lastActivityAt), {
                                  addSuffix: true,
                                  locale: es,
                                })}
                              </Typography>
                            </Box>
                          }
                          secondary={
                            <>
                              {conversation.conversationType && (
                                <Typography
                                  component="span"
                                  variant="body2"
                                  color="success.main"
                                  sx={{ display: "block", fontWeight: "medium" }}
                                >
                                  {conversation.conversationType}
                                </Typography>
                              )}
                              <Typography
                                component="span"
                                variant="body2"
                                color="text.primary"
                                sx={{
                                  display: "block",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                  maxWidth: "100%",
                                  fontWeight: conversation.unreadCount > 0 ? "medium" : "normal",
                                }}
                              >
                                {conversation.lastMessage?.content || "No hay mensajes"}
                              </Typography>
                            </>
                          }
                        />
                        {conversation.unreadCount > 0 && (
                          <Box
                            sx={{
                              minWidth: 20,
                              height: 20,
                              borderRadius: "50%",
                              backgroundColor: "success.main",
                              color: "white",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              fontSize: "0.75rem",
                              fontWeight: "bold",
                              ml: 1,
                            }}
                          >
                            {conversation.unreadCount}
                          </Box>
                        )}
                      </ListItemButton>
                      {index < filteredConversations.length - 1 && <Divider component="li" />}
                    </React.Fragment>
                  )
                })}
              </List>
            )}
          </>
        )}
      </Paper>
    </>
  )
}

export default EmbeddedChat
